import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, Phone, Clock, DollarSign, ChevronLeft, ChevronRight, Search, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CDRPage = () => {
  const { token } = useAuth();
  const [cdrs, setCdrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const pageSize = 20;

  const fetchCDRs = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const res = await axios.get(`${API}/user/cdr`, {
        params: { page, limit: pageSize },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setCdrs(res.data.cdrs || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch CDRs", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCDRs();
  }, [token, page]);

  const totalPages = Math.ceil(total / pageSize);

  const filteredCDRs = cdrs.filter(cdr => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (cdr.calledstation || cdr.dst || "").toLowerCase().includes(searchLower) ||
      (cdr.src || "").toLowerCase().includes(searchLower)
    );
  });

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div data-testid="cdr-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Call History</h1>
          <p className="text-slate-500 mt-1">View your call detail records (CDRs)</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 mb-6 border border-slate-200">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="cdr-search"
            />
          </div>
        </div>
      </div>

      {/* CDR Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : filteredCDRs.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Calls Found</h3>
            <p className="text-slate-500">Your call history will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cdr-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Date/Time</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">From</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">To</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Duration</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Cost</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredCDRs.map((cdr, i) => (
                  <motion.tr
                    key={cdr.id || i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700">
                          {cdr.starttime || cdr.calldate || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm text-slate-600">
                      {cdr.src || cdr.callerid || "Unknown"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-brand-400" />
                        <span className="font-mono text-sm text-slate-900">
                          {cdr.calledstation || cdr.dst || "Unknown"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {formatDuration(parseInt(cdr.sessiontime || cdr.billsec || 0))}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-brand-500 font-semibold">
                        <DollarSign className="w-4 h-4" />
                        {parseFloat(cdr.sessionbill || cdr.cost || 0).toFixed(4)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        (cdr.terminatecauseid === '16' || cdr.disposition === 'ANSWERED')
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {cdr.terminatecauseid === '16' || cdr.disposition === 'ANSWERED' ? 'Answered' : 'Failed'}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <span className="text-sm text-slate-500">
              Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CDRPage;
